/** Math-heavy worked examples for Limits / Taylor certificate boosts. */

export const LIMITS_P1_EXAMPLES = [
  {
    number: 1,
    tier: "Basic",
    title: "Direct substitution",
    setup: "Evaluate $\\lim_{x\\to 3}(2x^2-5)$.",
    steps: [
      { text: "Let $f(x)=2x^2-5$.", why: "Polynomial (continuous on $\\mathbb{R}$)." },
      { text: "Continuity at $a=3$ means $\\lim_{x\\to 3}f(x)=f(3)$.", why: "License to substitute." },
      { text: "Compute $f(3)=2(3)^2-5=2\\cdot 9-5=18-5=13$.", why: "Arithmetic after structure." },
      { text: "Hence $\\lim_{x\\to 3}(2x^2-5)=13$.", why: "Conclusion." },
      { text: "Check: no denominator vanishes at $x=3$.", why: "No removable/infinite issue." },
      { text: "Difference quotient of $f$ near $3$ approaches $f'(3)=12$, separate from the limit of $f$.", why: "Do not confuse $\\lim f$ with $\\lim f'$." },
      { text: "Graphically: the parabola $y=2x^2-5$ has height $13$ at $x=3$.", why: "Visual check." },
      { text: "Write: $\\displaystyle\\lim_{x\\to 3}(2x^2-5)=13$.", why: "Board form." },
    ],
    result: "$13$",
    check: "Substitution is legal for polynomials everywhere.",
    mistake: "Treating every rational the same way even when the denominator is $0$.",
  },
  {
    number: 2,
    tier: "Basic",
    title: "Factor and cancel",
    setup: "Evaluate $\\lim_{x\\to 2}\\dfrac{x^2-4}{x-2}$.",
    steps: [
      { text: "Direct sub: $\\dfrac{2^2-4}{2-2}=\\dfrac{0}{0}$ (indeterminate).", why: "Not a final answer." },
      { text: "Factor: $x^2-4=(x-2)(x+2)$.", why: "Difference of squares." },
      { text: "For $x\\neq 2$: $\\dfrac{(x-2)(x+2)}{x-2}=x+2$.", why: "Cancel in a deleted neighborhood." },
      { text: "So $\\lim_{x\\to 2}\\dfrac{x^2-4}{x-2}=\\lim_{x\\to 2}(x+2)$.", why: "Same limit after algebra." },
      { text: "$\\lim_{x\\to 2}(x+2)=4$.", why: "Continuous after cancel." },
      { text: "Hole at $(2,4)$ on $y=x+2$.", why: "Removable discontinuity of the original rational." },
      { text: "One-sided: both sides give $4$.", why: "Two-sided limit exists." },
      { text: "Final: $\\displaystyle\\lim_{x\\to 2}\\dfrac{x^2-4}{x-2}=4$.", why: "Board form." },
    ],
    result: "$4$",
    check: "Matches slope of the line after removing the hole.",
    mistake: "Declaring DNE because substitution gave $0/0$.",
  },
  {
    number: 3,
    tier: "Medium",
    title: "Rationalize",
    setup: "Evaluate $\\lim_{x\\to 0}\\dfrac{\\sqrt{1+x}-1}{x}$.",
    steps: [
      { text: "Sub: $\\dfrac{\\sqrt{1}-1}{0}=\\dfrac{0}{0}$.", why: "Indeterminate." },
      { text: "Multiply by conjugate $\\sqrt{1+x}+1$ over itself.", why: "Clear root difference." },
      { text: "Numerator: $(\\sqrt{1+x}-1)(\\sqrt{1+x}+1)=(1+x)-1=x$.", why: "Difference of squares." },
      { text: "Fraction becomes $\\dfrac{x}{x(\\sqrt{1+x}+1)}=\\dfrac{1}{\\sqrt{1+x}+1}$ for $x\\neq 0$.", why: "Cancel $x$." },
      { text: "$\\lim_{x\\to 0}\\dfrac{1}{\\sqrt{1+x}+1}=\\dfrac{1}{1+1}=\\dfrac{1}{2}$.", why: "Now continuous." },
      { text: "Same as $\\dfrac{d}{dx}\\sqrt{1+x}\\big|_{x=0}=\\dfrac{1}{2\\sqrt{1+x}}\\big|_{0}=\\tfrac12$.", why: "Definition of derivative." },
      { text: "For $x\\to 0^-$ with $x>-1$, same simplified expression works.", why: "Domain of $\\sqrt{1+x}$." },
      { text: "Final: $\\displaystyle\\lim_{x\\to 0}\\dfrac{\\sqrt{1+x}-1}{x}=\\dfrac12$.", why: "Board form." },
    ],
    result: "$\\dfrac{1}{2}$",
    check: "Matches $f'(0)$ for $f(x)=\\sqrt{1+x}$.",
    mistake: "Canceling $x$ without rationalizing first.",
  },
  {
    number: 4,
    tier: "Medium",
    title: "Two-path test (DNE)",
    setup: "Does $\\lim_{(x,y)\\to(0,0)}\\dfrac{xy}{x^2+y^2}$ exist?",
    steps: [
      { text: "Path $y=0$: $\\dfrac{x\\cdot 0}{x^2+0}=0\\to 0$.", why: "Along $x$-axis." },
      { text: "Path $x=0$: $\\dfrac{0\\cdot y}{0+y^2}=0\\to 0$.", why: "Along $y$-axis." },
      { text: "Path $y=x$: $\\dfrac{x\\cdot x}{x^2+x^2}=\\dfrac{x^2}{2x^2}=\\dfrac12$.", why: "Diagonal." },
      { text: "Limits $0$ and $\\tfrac12$ disagree.", why: "Path dependence." },
      { text: "Therefore the limit does not exist (DNE).", why: "Necessary condition failed." },
      { text: "Polar: $\\dfrac{r^2\\cos\\theta\\sin\\theta}{r^2}=\\tfrac12\\sin 2\\theta$ depends on $\\theta$.", why: "Confirms oscillation in angle." },
      { text: "Never conclude existence from axes alone.", why: "Common trap." },
      { text: "Final answer: DNE.", why: "Board form." },
    ],
    result: "Does not exist (DNE).",
    check: "Polar form $\\frac12\\sin 2\\theta$ is $\\theta$-dependent.",
    mistake: "Checking only axes and claiming the limit is $0$.",
  },
  {
    number: 5,
    tier: "Advanced",
    title: "Squeeze theorem",
    setup: "Evaluate $\\lim_{x\\to 0} x^2\\sin\\!\\dfrac{1}{x}$.",
    steps: [
      { text: "For all $x\\neq 0$: $-1\\le \\sin(1/x)\\le 1$.", why: "Sine bound." },
      { text: "Multiply by $x^2\\ge 0$: $-x^2\\le x^2\\sin(1/x)\\le x^2$.", why: "Inequality direction preserved." },
      { text: "$\\lim_{x\\to 0}(-x^2)=0$ and $\\lim_{x\\to 0}x^2=0$.", why: "Standard power limits." },
      { text: "Squeeze theorem: sandwiched function $\\to 0$.", why: "Both bounds share limit $0$." },
      { text: "Define $g(0)=0$ if desired; the limit ignores $x=0$.", why: "Limit vs value." },
      { text: "Oscillation of $\\sin(1/x)$ does not prevent the product limit.", why: "Amplitude $\\to 0$." },
      { text: "Same argument works as $x\\to 0^+$ and $x\\to 0^-$.", why: "Two-sided." },
      { text: "Final: $\\displaystyle\\lim_{x\\to 0}x^2\\sin(1/x)=0$.", why: "Board form." },
    ],
    result: "$0$",
    check: "Classic HEC squeeze example.",
    mistake: "Saying DNE because $\\sin(1/x)$ oscillates.",
  },
  {
    number: 6,
    tier: "Advanced",
    title: "Infinity / asymptote",
    setup: "Evaluate $\\lim_{x\\to\\infty}\\dfrac{3x^2+1}{2x^2-5}$.",
    steps: [
      { text: "Degrees equal (both $2$) => expect ratio of leading coeffs.", why: "End behavior rule." },
      { text: "Divide num and den by $x^2$: $\\dfrac{3+1/x^2}{2-5/x^2}$.", why: "Algebra for $\\infty/\\infty$." },
      { text: "As $x\\to\\infty$: $1/x^2\\to 0$, $5/x^2\\to 0$.", why: "Standard." },
      { text: "Limit $=\\dfrac{3+0}{2-0}=\\dfrac{3}{2}$.", why: "Continuous after rewrite." },
      { text: "Horizontal asymptote $y=3/2$.", why: "Interpretation." },
      { text: "Same limit as $x\\to-\\infty$ (even powers).", why: "Check both ends if asked." },
      { text: "Do not write $x=\\infty$ as a real number in substitution.", why: "Notation discipline." },
      { text: "Final: $\\displaystyle\\lim_{x\\to\\infty}\\dfrac{3x^2+1}{2x^2-5}=\\dfrac{3}{2}$.", why: "Board form." },
    ],
    result: "$\\dfrac{3}{2}$",
    check: "Equal degrees => leading-coefficient ratio.",
    mistake: "Canceling only one $x^2$ or writing $x=\\infty$.",
  },
  {
    number: 7,
    tier: "Advanced",
    title: "Scaled trig limit",
    setup: "Evaluate $\\lim_{x\\to 0}\\dfrac{\\sin(5x)}{x}$.",
    steps: [
      { text: "Want $\\sin u/u$ with $u=5x$.", why: "Standard limit form." },
      { text: "Write $\\dfrac{\\sin(5x)}{x}=5\\cdot\\dfrac{\\sin(5x)}{5x}$.", why: "Insert factor $5/5$." },
      { text: "As $x\\to 0$, $u=5x\\to 0$.", why: "Chain of limits." },
      { text: "$\\lim_{u\\to 0}\\dfrac{\\sin u}{u}=1$.", why: "Standard limit." },
      { text: "Therefore limit $=5\\cdot 1=5$.", why: "Constant factor." },
      { text: "Equivalent: $\\dfrac{d}{dx}\\sin(5x)\\big|_{0}=5\\cos 0=5$.", why: "Derivative check." },
      { text: "If it were $\\sin(5x)/(5x)$, answer would be $1$.", why: "Spot the missing $5$." },
      { text: "Final: $\\displaystyle\\lim_{x\\to 0}\\dfrac{\\sin(5x)}{x}=5$.", why: "Board form." },
    ],
    result: "$5$",
    check: "$(\\sin 5x)'(0)=5$.",
    mistake: "Answering $1$ and forgetting the chain factor $5$.",
  },
  {
    number: 8,
    tier: "Advanced",
    title: "One-sided continuity at endpoint",
    setup: "Is $f(x)=\\sqrt{x}$ continuous at $0$ on $[0,\\infty)$?",
    steps: [
      { text: "$f(0)=\\sqrt{0}=0$ is defined.", why: "Condition (1)." },
      { text: "Only $x\\ge 0$ is in the domain, so use $x\\to 0^+$.", why: "Domain-relative limit." },
      { text: "$\\lim_{x\\to 0^+}\\sqrt{x}=0$.", why: "Condition (2) one-sided." },
      { text: "Limit equals $f(0)$.", why: "Condition (3)." },
      { text: "Hence continuous at $0$ as a function on $[0,\\infty)$.", why: "Conclusion." },
      { text: "Two-sided limit in $\\mathbb{R}$ fails because left side is undefined for reals.", why: "Clarify setting." },
      { text: "Not differentiable at $0$: vertical tangent ($f'\\to\\infty$).", why: "Continuity $\\neq$ differentiability." },
      { text: "Final: yes, continuous at $0$ on its domain.", why: "Board form." },
    ],
    result: "Yes - continuous at $0$ on $[0,\\infty)$.",
    check: "Graph meets the origin with values approaching $0$.",
    mistake: "Requiring a two-sided limit when $x<0$ is outside the domain.",
  },
];

export const LIMITS_P2_EXAMPLES = [
  {
    number: 1,
    tier: "Basic",
    title: "Polynomial continuity",
    setup: "Is $f(x)=x^3-2x$ continuous at $x=1$?",
    steps: [
      { text: "$f(1)=1-2=-1$ is defined.", why: "Part (1)." },
      { text: "Polynomials are continuous on $\\mathbb{R}$.", why: "Sum/product of continuous maps." },
      { text: "$\\lim_{x\\to 1}f(x)=f(1)=-1$.", why: "Parts (2)-(3)." },
      { text: "All three continuity conditions hold.", why: "Definition." },
      { text: "Explicitly: $\\lim(x^3-2x)=1^3-2\\cdot 1=-1$.", why: "Substitution." },
      { text: "No piecewise break, no denominator.", why: "No special points." },
      { text: "Differentiable too: $f'(x)=3x^2-2$, but not required for continuity.", why: "Stronger property." },
      { text: "Final: continuous at $x=1$.", why: "Board form." },
    ],
    result: "Yes - continuous at $x=1$.",
    check: "No denominator or piecewise join.",
    mistake: "Thinking every continuity check needs an $\\varepsilon$-$\\delta$ write-up.",
  },
  {
    number: 2,
    tier: "Medium",
    title: "Removable discontinuity",
    setup: "$f(x)=\\dfrac{x^2-1}{x-1}$ for $x\\neq 1$, undefined at $1$. Continuous at $1$?",
    steps: [
      { text: "$f(1)$ is undefined => condition (1) fails.", why: "Already not continuous." },
      { text: "Factor: $x^2-1=(x-1)(x+1)$.", why: "Algebra." },
      { text: "For $x\\neq 1$: $f(x)=x+1$.", why: "Cancel." },
      { text: "$\\lim_{x\\to 1}f(x)=\\lim_{x\\to 1}(x+1)=2$.", why: "Limit exists." },
      { text: "Type: removable discontinuity (hole at $(1,2)$).", why: "Name the break." },
      { text: "Redefine $f(1)=2$ to make continuous.", why: "Repair." },
      { text: "After redefinition, $f(x)=x+1$ everywhere.", why: "Fully continuous." },
      { text: "Final: not continuous at $1$ as given (removable).", why: "Board form." },
    ],
    result: "Not continuous at $1$ (removable). Set $f(1)=2$ to fix.",
    check: "Graph is $y=x+1$ with a hole.",
    mistake: "Saying continuous after simplifying without defining $f(1)$.",
  },
  {
    number: 3,
    tier: "Medium",
    title: "Piecewise jump",
    setup: "$f(x)=\\begin{cases} x & x<0 \\\\ 1 & x\\ge 0 \\end{cases}$. Continuous at $0$?",
    steps: [
      { text: "$f(0)=1$ (right piece).", why: "Part (1) OK." },
      { text: "Left limit: $\\lim_{x\\to 0^-}f(x)=\\lim_{x\\to 0^-}x=0$.", why: "Use left formula." },
      { text: "Right limit: $\\lim_{x\\to 0^+}f(x)=1$.", why: "Use right formula." },
      { text: "Left $\\neq$ right => two-sided limit DNE.", why: "Part (2) fails." },
      { text: "Jump discontinuity of size $1$.", why: "Classification." },
      { text: "Cannot fix by changing only $f(0)$.", why: "Not removable." },
      { text: "Sketch: ray $y=x$ for $x<0$, then horizontal $y=1$ for $x\\ge 0$.", why: "Visual." },
      { text: "Final: discontinuous at $0$ (jump).", why: "Board form." },
    ],
    result: "Discontinuous at $0$ (jump).",
    check: "One-sided limits $0$ and $1$.",
    mistake: "Only checking $f(0)$ and the right-hand limit.",
  },
  {
    number: 4,
    tier: "Advanced",
    title: "IVT application",
    setup: "Show $x^3+x-1=0$ has a root in $(0,1)$.",
    steps: [
      { text: "Let $g(x)=x^3+x-1$.", why: "Continuous target." },
      { text: "$g$ continuous on $[0,1]$ (polynomial).", why: "IVT hypothesis." },
      { text: "$g(0)=-1<0$.", why: "Sign at left." },
      { text: "$g(1)=1+1-1=1>0$.", why: "Sign at right." },
      { text: "IVT: some $c\\in(0,1)$ with $g(c)=0$.", why: "Existence." },
      { text: "$g'(x)=3x^2+1>0$ => $g$ strictly increasing => unique root.", why: "Bonus uniqueness." },
      { text: "IVT alone does not give the decimal value of $c$.", why: "Scope of theorem." },
      { text: "Final: at least one root in $(0,1)$.", why: "Board form." },
    ],
    result: "At least one root in $(0,1)$ by IVT.",
    check: "Strict increase => uniqueness too.",
    mistake: "Claiming IVT finds the root's decimal expansion.",
  },
  {
    number: 5,
    tier: "Advanced",
    title: "Composition continuity",
    setup: "If $f$ is continuous at $b$ and $\\lim_{x\\to a}g(x)=b$, find $\\lim_{x\\to a}f(g(x))$.",
    steps: [
      { text: "Continuity of $f$ at $b$: $\\lim_{y\\to b}f(y)=f(b)$.", why: "Definition." },
      { text: "Given $\\lim g=b$.", why: "Inner limit." },
      { text: "Composition theorem: $\\lim f(g(x))=f(b)=f(\\lim g)$.", why: "Pass limit inside $f$." },
      { text: "Requires continuity of $f$ at the limit value $b$.", why: "Hypothesis check." },
      { text: "Counterexample if $f$ jumps at $b$: limit of $f\\circ g$ may fail.", why: "Necessity." },
      { text: "Example: $g(x)\\to 0$, $f=$ step at $0$ can break the swap.", why: "Classic trap." },
      { text: "Polynomials/exp/trig are continuous on $\\mathbb{R}$: safe to compose.", why: "Practical rule." },
      { text: "Final: $\\lim f(g)=f(b)$ under the stated hypotheses.", why: "Board form." },
    ],
    result: "$\\lim f(g(x))=f(b)$ when $f$ is continuous at $b$.",
    check: "Fails if $f$ is discontinuous at $b$.",
    mistake: "Passing limits inside discontinuous outer functions.",
  },
  {
    number: 6,
    tier: "Advanced",
    title: "$\\varepsilon$-$\\delta$ for linear $f$",
    setup: "Prove $\\lim_{x\\to 2}(3x+1)=7$.",
    steps: [
      { text: "Goal: given $\\varepsilon>0$, find $\\delta>0$ so $0<|x-2|<\\delta\\Rightarrow|(3x+1)-7|<\\varepsilon$.", why: "Definition." },
      { text: "Simplify: $|(3x+1)-7|=|3x-6|=3|x-2|$.", why: "Algebra." },
      { text: "Need $3|x-2|<\\varepsilon$, i.e. $|x-2|<\\varepsilon/3$.", why: "Solve inequality." },
      { text: "Choose $\\delta=\\varepsilon/3$.", why: "Works for all $\\varepsilon>0$." },
      { text: "If $0<|x-2|<\\delta$, then $3|x-2|<3\\cdot(\\varepsilon/3)=\\varepsilon$.", why: "Verify." },
      { text: "Hence the limit is $7$.", why: "Definition satisfied." },
      { text: "Slope factor $3$ forces $\\delta=\\varepsilon/3$, not $\\delta=\\varepsilon$.", why: "Common error." },
      { text: "Final: $\\delta=\\varepsilon/3$ proves the claim.", why: "Board form." },
    ],
    result: "Given $\\varepsilon>0$, take $\\delta=\\varepsilon/3$.",
    check: "Affine functions are continuous with $\\delta$ scaled by $|m|$.",
    mistake: "Picking $\\delta=\\varepsilon$ without the slope factor $3$.",
  },
  {
    number: 7,
    tier: "Advanced",
    title: "Absolute value continuity",
    setup: "Is $f(x)=|x|$ continuous at $0$?",
    steps: [
      { text: "$f(0)=0$ defined.", why: "Part (1)." },
      { text: "$\\lim_{x\\to 0^-}|x|=\\lim_{x\\to 0^-}(-x)=0$.", why: "Left." },
      { text: "$\\lim_{x\\to 0^+}|x|=\\lim_{x\\to 0^+}x=0$.", why: "Right." },
      { text: "Two-sided limit $=0=f(0)$.", why: "Parts (2)-(3)." },
      { text: "Continuous at $0$.", why: "Conclusion." },
      { text: "Not differentiable at $0$: left deriv $-1$, right $+1$.", why: "Corner." },
      { text: "$\\varepsilon$-$\\delta$: $| |x|-0 |=|x|<\\varepsilon$ with $\\delta=\\varepsilon$.", why: "Optional formal." },
      { text: "Final: continuous at $0$ (not differentiable).", why: "Board form." },
    ],
    result: "Yes - continuous at $0$ (not differentiable).",
    check: "V-shaped graph, no jump.",
    mistake: "Confusing continuity with differentiability.",
  },
  {
    number: 8,
    tier: "Advanced",
    title: "IVT uniqueness companion",
    setup: "Show $x^3+x-1=0$ has a unique root in $(0,1)$.",
    steps: [
      { text: "Existence: $g(0)=-1<0$, $g(1)=1>0$, IVT => a root.", why: "As in Ex. 4." },
      { text: "$g'(x)=3x^2+1\\ge 1>0$ for all $x$.", why: "Always positive." },
      { text: "$g$ strictly increasing on $\\mathbb{R}$.", why: "Positive derivative." },
      { text: "A strictly increasing continuous function crosses $0$ at most once.", why: "Uniqueness." },
      { text: "Hence exactly one real root, and it lies in $(0,1)$.", why: "Combine." },
      { text: "Numerically $c\\approx 0.68$, but proof does not need the decimal.", why: "Scope." },
      { text: "Cannot get uniqueness from IVT alone.", why: "Need monotonicity." },
      { text: "Final: unique root in $(0,1)$.", why: "Board form." },
    ],
    result: "Unique root in $(0,1)$ (IVT + $g'>0$).",
    check: "$g$ strictly increasing.",
    mistake: "Claiming uniqueness from IVT without a derivative/monotonicity argument.",
  },
];

export const TAYLOR_P1_EXAMPLES = [
  {
    number: 1,
    tier: "Basic",
    title: "Linearization",
    setup: "Linearize $f(x)=\\sqrt{x}$ at $a=4$.",
    steps: [
      { text: "$f(4)=\\sqrt{4}=2$.", why: "Value at expansion point." },
      { text: "$f'(x)=\\dfrac{1}{2\\sqrt{x}}$, so $f'(4)=\\dfrac{1}{4}$.", why: "Derivative." },
      { text: "Degree-1 Taylor: $P_1(x)=f(a)+f'(a)(x-a)$.", why: "Formula." },
      { text: "$P_1(x)=2+\\dfrac{1}{4}(x-4)$.", why: "Substitute." },
      { text: "This is the tangent line at $x=4$.", why: "Geometry." },
      { text: "At $x=4.41$: $P_1=2+\\frac14(0.41)=2.1025$, true $\\sqrt{4.41}=2.1$.", why: "Numeric check." },
      { text: "Error grows as $|x-4|$ grows.", why: "Local approximation." },
      { text: "Final: $P_1(x)=2+\\frac14(x-4)$.", why: "Board form." },
    ],
    result: "$2+\\dfrac{1}{4}(x-4)$",
    check: "Matches $f$ and $f'$ at $4$.",
    mistake: "Evaluating $f'$ at $x$ instead of at $a$.",
  },
  {
    number: 2,
    tier: "Basic",
    title: "Maclaurin for $e^x$",
    setup: "Write $P_3(x)$ for $e^x$ at $0$.",
    steps: [
      { text: "All derivatives of $e^x$ equal $e^x$.", why: "Key fact." },
      { text: "At $0$: $f^{(k)}(0)=1$ for every $k$.", why: "Evaluate." },
      { text: "Maclaurin: $P_n(x)=\\sum_{k=0}^n \\dfrac{f^{(k)}(0)}{k!}x^k$.", why: "Formula." },
      { text: "$P_3(x)=1+x+\\dfrac{x^2}{2!}+\\dfrac{x^3}{3!}$.", why: "Plug in." },
      { text: "$=1+x+\\dfrac{x^2}{2}+\\dfrac{x^3}{6}$.", why: "Simplify factorials." },
      { text: "Check: $P_3(0)=1=e^0$, $P_3'(0)=1$.", why: "Match value and slope." },
      { text: "Remainder involves $e^c x^4/24$ for some $c$ between $0$ and $x$.", why: "Preview Part 2." },
      { text: "Final: $P_3(x)=1+x+\\frac{x^2}{2}+\\frac{x^3}{6}$.", why: "Board form." },
    ],
    result: "$1+x+\\dfrac{x^2}{2}+\\dfrac{x^3}{6}$",
    check: "Coefficients $1/k!$.",
    mistake: "Forgetting factorials in denominators.",
  },
  {
    number: 3,
    tier: "Medium",
    title: "sin Maclaurin",
    setup: "Find $P_5(x)$ for $\\sin x$ at $0$.",
    steps: [
      { text: "Derivatives cycle: $\\sin,\\cos,-\\sin,-\\cos,\\ldots$.", why: "Pattern." },
      { text: "At $0$: $0,1,0,-1,0,1$ for orders $0$ through $5$.", why: "Evaluate." },
      { text: "Only odd powers survive (odd function).", why: "Symmetry." },
      { text: "$P_5(x)=x-\\dfrac{x^3}{3!}+\\dfrac{x^5}{5!}$.", why: "Assemble." },
      { text: "$=x-\\dfrac{x^3}{6}+\\dfrac{x^5}{120}$.", why: "Arithmetic." },
      { text: "Small-$x$: $\\sin x\\approx x$.", why: "Leading term." },
      { text: "Even powers must be absent; including $x^2$ is wrong.", why: "Trap." },
      { text: "Final: $x-\\frac{x^3}{6}+\\frac{x^5}{120}$.", why: "Board form." },
    ],
    result: "$x-\\dfrac{x^3}{6}+\\dfrac{x^5}{120}$",
    check: "Odd function => odd powers only.",
    mistake: "Including even powers.",
  },
  {
    number: 4,
    tier: "Medium",
    title: "cos Maclaurin",
    setup: "Find $P_4(x)$ for $\\cos x$ at $0$.",
    steps: [
      { text: "At $0$: $\\cos 0=1$, $-\\sin 0=0$, $-\\cos 0=-1$, $\\sin 0=0$, $\\cos 0=1$.", why: "Derivative table." },
      { text: "Even function => even powers only.", why: "Symmetry." },
      { text: "$P_4(x)=1-\\dfrac{x^2}{2!}+\\dfrac{x^4}{4!}$.", why: "Assemble." },
      { text: "$=1-\\dfrac{x^2}{2}+\\dfrac{x^4}{24}$.", why: "Simplify." },
      { text: "Differentiate: $-x+\\dfrac{x^3}{6}+\\cdots$ starts $-\\sin$ series.", why: "Consistency check." },
      { text: "Do not start with $x$ (that is $\\sin$).", why: "Trap." },
      { text: "At $x=0$: $P_4=1=\\cos 0$.", why: "Value match." },
      { text: "Final: $1-\\frac{x^2}{2}+\\frac{x^4}{24}$.", why: "Board form." },
    ],
    result: "$1-\\dfrac{x^2}{2}+\\dfrac{x^4}{24}$",
    check: "Derivative recovers $-\\sin$ Maclaurin start.",
    mistake: "Starting with $x$ as if it were $\\sin$.",
  },
  {
    number: 5,
    tier: "Medium",
    title: "Expand about $a\\neq 0$",
    setup: "Quadratic Taylor for $f(x)=\\ln x$ at $a=1$.",
    steps: [
      { text: "$f(1)=\\ln 1=0$.", why: "Value." },
      { text: "$f'(x)=1/x$, $f'(1)=1$.", why: "First derivative." },
      { text: "$f''(x)=-1/x^2$, $f''(1)=-1$.", why: "Second derivative." },
      { text: "$P_2(x)=f(1)+f'(1)(x-1)+\\dfrac{f''(1)}{2}(x-1)^2$.", why: "Formula." },
      { text: "$P_2(x)=0+1\\cdot(x-1)+\\dfrac{-1}{2}(x-1)^2=(x-1)-\\dfrac12(x-1)^2$.", why: "Substitute." },
      { text: "Powers of $(x-1)$, not bare $x$.", why: "Shifted center." },
      { text: "At $x=1.1$: $P_2=0.1-0.5(0.01)=0.095$; $\\ln 1.1\\approx 0.0953$.", why: "Check." },
      { text: "Final: $(x-1)-\\frac12(x-1)^2$.", why: "Board form." },
    ],
    result: "$(x-1)-\\dfrac{1}{2}(x-1)^2$",
    check: "Close to $\\ln 1.1$ near $a=1$.",
    mistake: "Expanding in powers of $x$ instead of $(x-1)$.",
  },
  {
    number: 6,
    tier: "Advanced",
    title: "Geometric series link",
    setup: "From $1/(1-x)$, obtain the series for $-\\ln(1-x)$.",
    steps: [
      { text: "For $|x|<1$: $\\dfrac{1}{1-x}=\\sum_{n=0}^\\infty x^n$.", why: "Geometric series." },
      { text: "Integrate termwise from $0$ to $x$: $\\int_0^x\\dfrac{1}{1-t}\\,dt=-\\ln(1-x)$.", why: "Left side." },
      { text: "Right: $\\sum_{n=0}^\\infty\\int_0^x t^n\\,dt=\\sum_{n=0}^\\infty \\dfrac{x^{n+1}}{n+1}$.", why: "Termwise." },
      { text: "Reindex $k=n+1$: $-\\ln(1-x)=\\sum_{k=1}^\\infty \\dfrac{x^k}{k}$ for $|x|<1$.", why: "Standard log series." },
      { text: "Equivalently $\\ln(1-x)=-\\sum_{k=1}^\\infty x^k/k$.", why: "Sign form." },
      { text: "Differentiate the log series to recover geometric series.", why: "Check." },
      { text: "Endpoint $x=1$ needs separate alternating-series care ($\\ln 2$).", why: "Do not plug blindly." },
      { text: "Final: $\\ln(1-x)=-\\sum_{n=1}^\\infty x^n/n$ ($|x|<1$).", why: "Board form." },
    ],
    result: "$\\ln(1-x)=-\\sum_{n=1}^\\infty \\dfrac{x^n}{n}$ for $|x|<1$",
    check: "Differentiate both sides.",
    mistake: "Using the series at $x=1$ without checking convergence.",
  },
  {
    number: 7,
    tier: "Advanced",
    title: "Product of known series",
    setup: "Find Maclaurin terms through degree $3$ for $e^x\\sin x$.",
    steps: [
      { text: "$e^x=1+x+\\dfrac{x^2}{2}+\\dfrac{x^3}{6}+O(x^4)$.", why: "Known." },
      { text: "$\\sin x=x-\\dfrac{x^3}{6}+O(x^5)$.", why: "Known." },
      { text: "Multiply, keep degree $\\le 3$: $(1+x+x^2/2+x^3/6)(x-x^3/6)+\\cdots$.", why: "Cauchy product truncated." },
      { text: "Collect: $x+x^2+\\left(\\frac12-\\frac16\\right)x^3+\\cdots=x+x^2+\\frac13 x^3+O(x^4)$.", why: "Coefficients." },
      { text: "Alternatively differentiate $e^x\\sin x$ three times at $0$.", why: "Direct Taylor." },
      { text: "At $0$: $f=0$, $f'=1$, $f''=2$, $f'''=2$ => $P_3=x+x^2+\\frac13 x^3$.", why: "Matches." },
      { text: "Do not multiply infinite series carelessly past the needed degree.", why: "Bookkeeping." },
      { text: "Final: $x+x^2+\\frac13 x^3+O(x^4)$.", why: "Board form." },
    ],
    result: "$x+x^2+\\dfrac{1}{3}x^3+O(x^4)$",
    check: "Matches derivative table at $0$.",
    mistake: "Losing cross terms when multiplying series.",
  },
  {
    number: 8,
    tier: "Advanced",
    title: "Shifted exponential",
    setup: "Write $P_2(x)$ for $e^{2x}$ at $a=0$.",
    steps: [
      { text: "Replace $u=2x$ in $e^u=1+u+u^2/2+\\cdots$.", why: "Composition." },
      { text: "$e^{2x}=1+2x+\\dfrac{(2x)^2}{2}+O(x^3)=1+2x+2x^2+O(x^3)$.", why: "Expand." },
      { text: "Direct: $f'=2e^{2x}$, $f''=4e^{2x}$; at $0$: $1,2,4$.", why: "Verify." },
      { text: "$P_2=1+\\dfrac{2}{1!}x+\\dfrac{4}{2!}x^2=1+2x+2x^2$.", why: "Formula." },
      { text: "Factor: $e^{2x}=(e^x)^2$ series multiply also works.", why: "Alternate path." },
      { text: "Radius of convergence remains $\\infty$.", why: "Entire function." },
      { text: "Common error: writing $1+2x+\\frac{(2x)^2}{2!}$ then forgetting $(2)^2=4$.", why: "Trap." },
      { text: "Final: $P_2(x)=1+2x+2x^2$.", why: "Board form." },
    ],
    result: "$1+2x+2x^2$",
    check: "$f''(0)/2!=4/2=2$.",
    mistake: "Using $(2x)^2/2!$ incorrectly as $2x^2/2=x^2$.",
  },
];

export const TAYLOR_P2_EXAMPLES = [
  {
    number: 1,
    tier: "Medium",
    title: "Alternating series error",
    setup: "Approximate $\\ln(1.1)$ by $x-x^2/2$ ($a=0$) and bound the error.",
    steps: [
      { text: "Use $\\ln(1+x)=x-x^2/2+x^3/3-\\cdots$ at $x=0.1$.", why: "Alternating for $x\\in(0,1]$." },
      { text: "Partial sum $S_2=0.1-\\dfrac{(0.1)^2}{2}=0.1-0.005=0.095$.", why: "Two terms." },
      { text: "Next term $a_3=\\dfrac{(0.1)^3}{3}=\\dfrac{0.001}{3}\\approx 3.33\\times 10^{-4}$.", why: "First omitted." },
      { text: "Alternating series estimation: $|R|\\le |a_3|$.", why: "Hypothesis OK for this $x$." },
      { text: "True $\\ln(1.1)\\approx 0.095310$; error $\\approx 3.1\\times 10^{-4}$.", why: "Check." },
      { text: "Bound $\\approx 3.4\\times 10^{-4}$ safely covers the true error.", why: "Conservative." },
      { text: "Need smaller error => include $x^3/3$.", why: "Tradeoff." },
      { text: "Final: approx $0.095$ with error $\\lesssim 3.4\\times 10^{-4}$.", why: "Board form." },
    ],
    result: "Approx $0.095$; error $\\le$ about $3.4\\times 10^{-4}$.",
    check: "True value $\\approx 0.09531$.",
    mistake: "Using alternating bound when terms are not eventually decreasing in abs value.",
  },
  {
    number: 2,
    tier: "Medium",
    title: "Ratio test / radius",
    setup: "Find the radius of convergence of $\\sum_{n=1}^\\infty n x^n$.",
    steps: [
      { text: "Let $a_n=n x^n$.", why: "General term." },
      { text: "$\\left|\\dfrac{a_{n+1}}{a_n}\\right|=\\dfrac{n+1}{n}|x|\\to |x|$.", why: "Ratio." },
      { text: "Converges when $\\lim|a_{n+1}/a_n|<1$, i.e. $|x|<1$.", why: "Ratio test." },
      { text: "Radius $R=1$.", why: "Open interval $(-1,1)$." },
      { text: "At $x=1$: $\\sum n$ diverges.", why: "Endpoint." },
      { text: "At $x=-1$: $\\sum n(-1)^n$ diverges.", why: "Endpoint." },
      { text: "Sum equals $x/(1-x)^2$ on $(-1,1)$.", why: "Closed form." },
      { text: "Final: radius $1$; converges only for $|x|<1$.", why: "Board form." },
    ],
    result: "Radius $1$ (converges for $|x|<1$ only).",
    check: "Known generating function $x/(1-x)^2$.",
    mistake: "Forgetting separate endpoint tests.",
  },
  {
    number: 3,
    tier: "Advanced",
    title: "Lagrange remainder for cos",
    setup: "Bound the error of $P_2(x)=1-x^2/2$ for $\\cos x$ on $|x|\\le 0.1$.",
    steps: [
      { text: "Next derivative in Lagrange form involves order $3$ or $4$ depending on indexing.", why: "Careful with $n$." },
      { text: "Using $R_3(x)=\\dfrac{f^{(4)}(c)}{4!}x^4$ after matching through degree $3$ odd zero term.", why: "Even function." },
      { text: "$f^{(4)}=\\cos$, so $|f^{(4)}(c)|\\le 1$.", why: "Bound." },
      { text: "$|R|\\le \\dfrac{|x|^4}{24}\\le \\dfrac{(0.1)^4}{24}=\\dfrac{0.0001}{24}\\approx 4.17\\times 10^{-6}$.", why: "Worst case on interval." },
      { text: "So error under about $4.2\\times 10^{-6}$.", why: "Conclusion." },
      { text: "Enough for many engineering tolerances.", why: "Interpretation." },
      { text: "Keep $n$ consistent between polynomial degree and remainder index.", why: "Trap." },
      { text: "Final: $|R|\\le 4.2\\times 10^{-6}$ on $|x|\\le 0.1$.", why: "Board form." },
    ],
    result: "Error $\\le$ about $4.2\\times 10^{-6}$.",
    check: "Uses $|\\cos c|\\le 1$.",
    mistake: "Mixing up the factorial in the remainder with the polynomial degree.",
  },
  {
    number: 4,
    tier: "Advanced",
    title: "Binomial linearization",
    setup: "Linearize $\\sqrt{1+x}$ for small $x$.",
    steps: [
      { text: "Binomial/Taylor: $(1+x)^{1/2}=1+\\dfrac12 x+\\dfrac{(1/2)(-1/2)}{2}x^2+\\cdots$.", why: "General binomial." },
      { text: "First-order: $\\sqrt{1+x}\\approx 1+\\dfrac{x}{2}$.", why: "Drop $x^2$ and higher." },
      { text: "Second-order correction $-x^2/8$.", why: "Optional accuracy." },
      { text: "At $x=0.01$: linear $1.005$; true $\\sqrt{1.01}\\approx 1.004987$.", why: "Check." },
      { text: "Derivative at $0$ is $1/2$, not $1$.", why: "Trap ($1+x$ would be wrong)." },
      { text: "Valid for $|x|$ small; radius $1$ for the full series.", why: "Domain." },
      { text: "Physics: small relative change approximations.", why: "Context." },
      { text: "Final: $\\sqrt{1+x}\\approx 1+x/2$ for small $x$.", why: "Board form." },
    ],
    result: "$\\sqrt{1+x}\\approx 1+\\dfrac{x}{2}$ (small $x$)",
    check: "Matches $f'(0)=1/2$.",
    mistake: "Using $1+x$ as if the derivative were $1$.",
  },
  {
    number: 5,
    tier: "Advanced",
    title: "$e^{-x^2}$ series",
    setup: "Series for $e^{-x^2}$ about $0$ through degree $4$.",
    steps: [
      { text: "Start from $e^u=1+u+\\dfrac{u^2}{2}+\\dfrac{u^3}{6}+\\cdots$.", why: "Base series." },
      { text: "Set $u=-x^2$: $e^{-x^2}=1+(-x^2)+\\dfrac{(-x^2)^2}{2}+\\dfrac{(-x^2)^3}{6}+\\cdots$.", why: "Compose." },
      { text: "$=1-x^2+\\dfrac{x^4}{2}-\\dfrac{x^6}{6}+\\cdots$.", why: "Simplify." },
      { text: "Through degree $4$: $1-x^2+\\dfrac12 x^4+O(x^6)$.", why: "Truncate." },
      { text: "Even function => only even powers.", why: "Symmetry check." },
      { text: "Do not write $x^4/2!$ confusing $u^2/2!$ with bare $x^4$.", why: "Factorial trap: $u^2=(-x^2)^2=x^4$, over $2!=2$." },
      { text: "Gaussian integrals and probability use this expansion.", why: "Context." },
      { text: "Final: $1-x^2+\\frac12 x^4+O(x^6)$.", why: "Board form." },
    ],
    result: "$1-x^2+\\dfrac{1}{2}x^4+O(x^6)$",
    check: "Even powers only.",
    mistake: "Wrong factorial bookkeeping on $(-x^2)^2$.",
  },
  {
    number: 6,
    tier: "Advanced",
    title: "Smooth but not analytic",
    setup: "Name a $C^\\infty$ function whose Taylor series at $0$ is $0$ but $f\\not\\equiv 0$.",
    steps: [
      { text: "Classic: $f(x)=e^{-1/x^2}$ for $x>0$, $f(x)=0$ for $x\\le 0$ (one-sided flat).", why: "Standard bump building block." },
      { text: "All derivatives at $0$ vanish: $f^{(n)}(0)=0$.", why: "Proof by induction / asymptotic." },
      { text: "Taylor series at $0$ is the zero series.", why: "All coeffs $0$." },
      { text: "But $f(x)>0$ for $x>0$, so $f\\not\\equiv 0$.", why: "Series $\\neq$ function." },
      { text: "Remainder does not tend to $0$ in a way that recovers $f$.", why: "Not analytic." },
      { text: "Lesson: $C^\\infty$ $\\neq$ analytic.", why: "Conceptual." },
      { text: "HEC stretch: state the distinction clearly.", why: "Exam language." },
      { text: "Final: flat bump at $0$; series $0$, function not identically $0$.", why: "Board form." },
    ],
    result: "e.g. $e^{-1/x^2}$ (flat at $0$); series is $0$ but $f\\not\\equiv 0$.",
    check: "All derivatives at $0$ are $0$.",
    mistake: "Assuming every infinitely differentiable $f$ equals its Taylor series.",
  },
  {
    number: 7,
    tier: "Advanced",
    title: "How many terms for a tolerance",
    setup: "How large must $n$ be so $|R_n(0.5)|\\le 10^{-4}$ for $e^x$ at $a=0$?",
    steps: [
      { text: "Lagrange: $|R_n(x)|\\le \\dfrac{e^{|x|}}{(n+1)!}|x|^{n+1}$ (bound $e^c\\le e^{|x|}$ on $[0,x]$).", why: "Remainder form." },
      { text: "Here $|x|=0.5$, need $\\dfrac{e^{0.5}}{(n+1)!}(0.5)^{n+1}\\le 10^{-4}$.", why: "Inequality." },
      { text: "$e^{0.5}\\approx 1.65$.", why: "Bound constant." },
      { text: "Try $n=5$: $\\dfrac{1.65}{720}(0.5)^6\\approx 0.0023\\cdot 0.0156\\approx 3.6\\times 10^{-5}$.", why: "Compute." },
      { text: "Already $<10^{-4}$ at $n=5$; $n=4$ may be borderline - check.", why: "Search $n$." },
      { text: "$n=4$: $\\dfrac{1.65}{120}(0.5)^5\\approx 0.01375\\cdot 0.03125\\approx 4.3\\times 10^{-4}>10^{-4}$.", why: "Too big." },
      { text: "So $n\\ge 5$ suffices with this bound.", why: "Answer." },
      { text: "Final: take degree at least $5$.", why: "Board form." },
    ],
    result: "$n\\ge 5$ guarantees the bound (with this estimate).",
    check: "Direct computation of factorial powers.",
    mistake: "Forgetting the $(n+1)!$ growth that eventually wins.",
  },
  {
    number: 8,
    tier: "Advanced",
    title: "Integrate a Taylor polynomial",
    setup: "Approximate $\\int_0^{0.2}\\cos x\\,dx$ using $P_4$ for $\\cos$.",
    steps: [
      { text: "$\\cos x\\approx 1-\\dfrac{x^2}{2}+\\dfrac{x^4}{24}$ on a small interval.", why: "Maclaurin $P_4$." },
      { text: "Integrate: $\\int_0^{0.2}\\!\\left(1-\\dfrac{x^2}{2}+\\dfrac{x^4}{24}\\right)dx=\\left[x-\\dfrac{x^3}{6}+\\dfrac{x^5}{120}\\right]_0^{0.2}$.", why: "Termwise." },
      { text: "$=0.2-\\dfrac{(0.2)^3}{6}+\\dfrac{(0.2)^5}{120}$.", why: "Evaluate." },
      { text: "$(0.2)^3=0.008$, $/6\\approx 0.001333$; $(0.2)^5=0.00032$, $/120\\approx 0.00000267$.", why: "Arithmetic." },
      { text: "Approx $0.2-0.001333+0.000003=0.198670$.", why: "Sum." },
      { text: "True $\\sin 0.2\\approx 0.198669$.", why: "FTC: $\\int_0^{0.2}\\cos=\\sin 0.2$." },
      { text: "Error tiny; remainder analysis optional for certificate rigor.", why: "Quality." },
      { text: "Final: $\\approx 0.19867$.", why: "Board form." },
    ],
    result: "$\\approx 0.19867$ (matches $\\sin 0.2$ closely)",
    check: "FTC exact value is $\\sin 0.2$.",
    mistake: "Integrating the series but changing limits incorrectly.",
  },
];
